import { AppRoutes } from '../../common/constants';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { FnaListItem, FnaState } from '../../common/types/analyses';
import { GridColDef } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { JSX, useMemo, useState } from 'react';
import { useArchiveFna } from '../../common/components/mutations';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DataGrid, { DataGridProps } from '../../common/components/dataGrid';
import DataGridLoadingState from '../../common/components/dataGrid/dataGridLoadingState';
import DataGridSelectAction from '../../common/components/dataGrid/dataGridSelectAction';
import FnaDetailsCell from './cellRenderers/fnaDescriptionCell';

interface ActionsButtonProps {
  /**
   * Callback for clicking edit
   */
  onClickEdit: () => void;
  /**
   * Callback for clicking delete
   */
  onClickDelete: () => void;
  /**
   * Callback for clicking quote
   */
  onClickQuote?: () => void;
}

const ActionsButton = ({
  onClickEdit,
  onClickDelete,
  onClickQuote,
}: ActionsButtonProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (callback: () => void): void => {
    setAnchorEl(null);
    callback();
  };

  return (
    <Box key="actions" className={open ? 'active' : ''}>
      <IconButton onClick={(event): void => setAnchorEl(event.currentTarget)}>
        <Icon icon="mdi:dots-vertical" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={(): void => setAnchorEl(null)}>
        <MenuItem onClick={(): void => handleClick(onClickEdit)}>Edit</MenuItem>
        {onClickQuote && <MenuItem onClick={(): void => handleClick(onClickQuote)}>Quote</MenuItem>}
        <MenuItem onClick={(): void => handleClick(onClickDelete)}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};

interface FNAsProps extends Omit<DataGridProps<FnaListItem>, 'rows' | 'columns'> {
  /**
   * Flag to indicate if FNAs are editable
   */
  editable: boolean;
  /**
   * Roleplayer ID to filter FNAs by
   */
  roleplayerId?: number;
  /**
   * Optional array of selected fna ids
   */
  selectedIds?: number[];
  /**
   * Callback to be called when the select button is clicked
   */
  onClickSelect?: (fna: FnaListItem) => void;
}

/**
 * List of FNAs
 */
const FNAs = ({
  editable,
  roleplayerId,
  selectedIds,
  onClickSelect,
  ...props
}: FNAsProps): JSX.Element => {
  const navigate = useNavigate();
  const { mutate: archiveFna, isPending: isArchivingInFlight } = useArchiveFna();
  const { data, isLoading } = useQuery<FnaListItem[]>({
    queryKey: [
      '/FNALists',
      {
        RoleplayerIDList: roleplayerId,
        StateIDList: [FnaState.Started, FnaState.Quoted, FnaState.NeedsID].join(','),
      },
    ],
  });

  // Sort FNAs so that selected items are on top
  const fnasWithSelectedOnTop = useMemo(() => {
    return data && selectedIds && selectedIds.length > 0
      ? [...data].sort((a) => (selectedIds.includes(a.id) ? -1 : 0))
      : data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const columns: GridColDef<FnaListItem>[] = [
    {
      field: 'roleplayerList',
      headerName: 'For',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.roleplayerList || '-',
      renderCell: FnaDetailsCell,
    },
    {
      field: 'description',
      headerName: 'Description',
    },
    {
      field: 'created',
      headerName: 'Created at',
    },
  ];

  if (editable) {
    columns.push({
      field: 'actions',
      type: 'actions',
      renderCell: ({ row }) => {
        const needsCount =
          Number(row.suretyshipCount) + Number(row.keypersonCount) + Number(row.buyandsellCount);
        return (
          <ActionsButton
            onClickEdit={(): void => navigate(`${AppRoutes.FNAs}/${row.id}`)}
            onClickQuote={
              needsCount > 0
                ? (): void => navigate(`${AppRoutes.FNAs}/${row.id}/quotes`)
                : undefined
            }
            onClickDelete={(): void => archiveFna(row.id)}
          />
        );
      },
    });
  } else if (onClickSelect) {
    columns.push({
      field: 'actions',
      type: 'actions',
      renderCell: ({ row }) => {
        return (
          <Box className="active">
            <DataGridSelectAction
              active={selectedIds?.includes(row.id)}
              onClick={(): void => onClickSelect(row)}
              key="select"
            />
          </Box>
        );
      },
    });
  }

  if (!fnasWithSelectedOnTop || isLoading) {
    return <DataGridLoadingState />;
  }

  return (
    <DataGrid
      {...props}
      rows={fnasWithSelectedOnTop}
      columns={columns}
      loading={isArchivingInFlight}
      initialState={{
        columns: {
          columnVisibilityModel: {
            description: false,
            created: false,
          },
        },
        sorting: {
          sortModel: [{ field: 'created', sort: 'desc' }],
        },
      }}
    />
  );
};

export default FNAs;
